import { ref } from 'vue'
import jsPDF from 'jspdf'

export function usePDFGeneration() {
  const showPDFPreview = ref(false)
  const previewPDFUrl = ref('')
  const previewPDF = ref(null)
  
  const exportToPDF = async ({
    paperFormat,
    columnCount,
    showTitle,
    showDescription,
    showMetadata,
    showHeaderImage,
    headerImageOpacity,
    showImages,
    currentList,
    groupedItems,
    totalItems,
    checkedItems,
    getHeaderImageUrl,
    getPacklistDisplayName,
    formatDate,
    getGroupData,
    getGroupImageUrl,
    getCalculatedQuantity
  }) => {
    try {
      // Create PDF with appropriate format
      const pdfFormat = paperFormat.toLowerCase() === 'a4' ? 'a4' : 
                       paperFormat.toLowerCase() === 'letter' ? 'letter' : 'legal'
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: pdfFormat
      })

      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 15
      const contentWidth = pageWidth - (2 * margin)
      let yPosition = margin

      // Add header image as background if enabled
      if (currentList?.header_image_url && showHeaderImage) {
        try {
          const headerImageUrl = getHeaderImageUrl()
          if (headerImageUrl) {
            // Create image element to get dimensions
            const img = new Image()
            img.crossOrigin = 'anonymous'
            
            await new Promise((resolve, reject) => {
              img.onload = resolve
              img.onerror = reject
              img.src = headerImageUrl
            })

            // Calculate image dimensions to fit full width
            const imgAspectRatio = img.width / img.height
            const imgWidth = pageWidth
            const imgHeight = pageWidth / imgAspectRatio

            // Add image at top-left (0,0) with full width
            pdf.addImage(img, 'JPEG', 0, 0, imgWidth, imgHeight, undefined, 'NONE')
            
            // Set opacity by adding a semi-transparent white rectangle over the image
            if (headerImageOpacity < 100) {
              const opacity = 1 - (headerImageOpacity / 100)
              pdf.setFillColor(255, 255, 255)
              pdf.setGState(pdf.GState({ opacity: opacity }))
              pdf.rect(0, 0, pageWidth, pageHeight, 'F')
              pdf.setGState(pdf.GState({ opacity: 1 })) // Reset opacity
            }
          }
        } catch (error) {
          console.warn('Failed to load header image for PDF:', error)
        }
      }

      // Set font
      pdf.setFont('helvetica')

      // Add header if enabled
      if (showTitle || showDescription || showMetadata) {
        if (showTitle) {
          pdf.setFontSize(20)
          pdf.setFont('helvetica', 'bold')
          const title = getPacklistDisplayName(currentList) || 'Packlist'
          pdf.text(title, pageWidth / 2, yPosition, { align: 'center' })
          yPosition += 12
        }

        if (showDescription && currentList?.description) {
          pdf.setFontSize(12)
          pdf.setFont('helvetica', 'normal')
          const description = currentList.description
          const lines = pdf.splitTextToSize(description, contentWidth)
          pdf.text(lines, pageWidth / 2, yPosition, { align: 'center' })
          yPosition += (lines.length * 6) + 5
        }

        if (showMetadata) {
          pdf.setFontSize(10)
          pdf.setFont('helvetica', 'normal')
          const metadata = [
            formatDate(new Date()),
            `${currentList?.duration || 1} day${(currentList?.duration || 1) !== 1 ? 's' : ''}`,
            `${totalItems} items`,
            checkedItems > 0 ? `${checkedItems} checked` : null
          ].filter(Boolean).join(' • ')
          pdf.text(metadata, pageWidth / 2, yPosition, { align: 'center' })
          yPosition += 8
        }

        // Add separator line
        pdf.line(margin, yPosition, pageWidth - margin, yPosition)
        yPosition += 8
      }

      // Calculate column layout
      const colWidth = (contentWidth - ((columnCount - 1) * 5)) / columnCount
      const colPositions = []
      for (let i = 0; i < columnCount; i++) {
        colPositions.push(margin + (i * (colWidth + 5)))
      }

      let currentCol = 0
      let colY = yPosition

      // Process groups
      for (const group of groupedItems) {
        let groupStartY = colY

        // Group header with optional image background
        const groupTitle = group.name || 'General'
        const groupData = getGroupData(group.name)
        
        if (showImages && groupData?.image_url) {
          try {
            // Load group image
            const groupImageUrl = getGroupImageUrl(groupData)
            if (groupImageUrl) {
              const groupImg = new Image()
              groupImg.crossOrigin = 'anonymous'
              
              await new Promise((resolve, reject) => {
                groupImg.onload = resolve
                groupImg.onerror = reject
                groupImg.src = groupImageUrl
              })

              // Calculate image dimensions for group header background
              const headerHeight = 12 // Height for group header background
              const imgAspectRatio = groupImg.width / groupImg.height
              const imgWidth = colWidth
              const imgHeight = Math.min(headerHeight, colWidth / imgAspectRatio)

              // Add group image as background
              pdf.addImage(groupImg, 'JPEG', colPositions[currentCol], colY - 4, imgWidth, imgHeight, undefined, 'NONE')
              
              // Add semi-transparent overlay for text readability
              pdf.setFillColor(0, 0, 0)
              pdf.setGState(pdf.GState({ opacity: 0.3 }))
              pdf.rect(colPositions[currentCol], colY - 4, imgWidth, imgHeight, 'F')
              pdf.setGState(pdf.GState({ opacity: 1 })) // Reset opacity

              // Group title with white text for contrast
              pdf.setFontSize(14)
              pdf.setFont('helvetica', 'bold')
              pdf.setTextColor(255, 255, 255) // White text
              pdf.text(groupTitle, colPositions[currentCol] + (colWidth / 2), colY + 2, { align: 'center' })
              pdf.setTextColor(0, 0, 0) // Reset to black
              
              colY += imgHeight + 2
            } else {
              // Fallback to simple text header
              pdf.setFontSize(14)
              pdf.setFont('helvetica', 'bold')
              pdf.text(groupTitle, colPositions[currentCol], colY)
              colY += 8

              // Draw underline
              pdf.setLineWidth(0.5)
              pdf.line(colPositions[currentCol], colY - 2, colPositions[currentCol] + colWidth, colY - 2)
              colY += 3
            }
          } catch (error) {
            console.warn('Failed to load group image for PDF:', error)
            // Fallback to simple text header
            pdf.setFontSize(14)
            pdf.setFont('helvetica', 'bold')
            pdf.text(groupTitle, colPositions[currentCol], colY)
            colY += 8

            // Draw underline
            pdf.setLineWidth(0.5)
            pdf.line(colPositions[currentCol], colY - 2, colPositions[currentCol] + colWidth, colY - 2)
            colY += 3
          }
        } else {
          // Simple text header when images are disabled
          pdf.setFontSize(14)
          pdf.setFont('helvetica', 'bold')
          pdf.text(groupTitle, colPositions[currentCol], colY)
          colY += 8

          // Draw underline
          pdf.setLineWidth(0.5)
          pdf.line(colPositions[currentCol], colY - 2, colPositions[currentCol] + colWidth, colY - 2)
          colY += 3
        }

        // Group items
        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')

        for (const item of group.items) {
          // Check if we need to move to next column or page
          if (colY > pageHeight - 30) {
            currentCol++
            if (currentCol >= columnCount) {
              // New page
              pdf.addPage()
              currentCol = 0
              colY = margin
            } else {
              colY = yPosition
            }
          }

          // Checkbox
          pdf.rect(colPositions[currentCol], colY - 3, 3, 3)
          if (item.is_checked) {
            pdf.text('✓', colPositions[currentCol] + 0.5, colY)
          }

          // Item name with quantity
          let itemText = item.name
          const quantity = getCalculatedQuantity(item)
          if (quantity > 1) {
            itemText += ` (${quantity}x)`
          }

          // Handle strikethrough for checked items
          const x = colPositions[currentCol] + 5
          if (item.is_checked) {
            pdf.setTextColor(150, 150, 150)
            pdf.text(itemText, x, colY)
            // Add strikethrough line
            const textWidth = pdf.getTextWidth(itemText)
            pdf.line(x, colY - 1, x + textWidth, colY - 1)
            pdf.setTextColor(0, 0, 0) // Reset color
          } else {
            pdf.text(itemText, x, colY)
          }

          colY += 5

          // Item description
          if (item.description) {
            pdf.setFontSize(8)
            pdf.setTextColor(100, 100, 100)
            const descLines = pdf.splitTextToSize(item.description, colWidth - 10)
            pdf.text(descLines, x + 2, colY)
            colY += (descLines.length * 3) + 1
            pdf.setFontSize(10)
            pdf.setTextColor(0, 0, 0)
          }
        }

        colY += 5 // Space after group

        // If group is too tall for remaining space, move to next column
        if (colY - groupStartY > (pageHeight - yPosition - 30) / 2 && currentCol < columnCount - 1) {
          currentCol++
          colY = yPosition
        }
      }

      // Generate filename for later use
      const listName = currentList?.name || 'Packlist'
      const sanitizedName = listName.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `${sanitizedName}_${timestamp}.pdf`

      // Store PDF and show preview
      previewPDF.value = { pdf, filename }
      const pdfBlob = new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' })
      previewPDFUrl.value = URL.createObjectURL(pdfBlob)
      showPDFPreview.value = true

    } catch (error) {
      console.error('Error generating PDF:', error)
      throw new Error('Failed to generate PDF. Please try again.')
    }
  }

  const downloadPDF = () => {
    if (previewPDF.value) {
      previewPDF.value.pdf.save(previewPDF.value.filename)
      closePDFPreview()
    }
  }

  const printPDF = () => {
    if (previewPDFUrl.value) {
      const printWindow = window.open(previewPDFUrl.value, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
    }
  }

  const closePDFPreview = () => {
    showPDFPreview.value = false
    if (previewPDFUrl.value) {
      URL.revokeObjectURL(previewPDFUrl.value)
      previewPDFUrl.value = ''
    }
    previewPDF.value = null
  }

  const handlePDFError = () => {
    console.warn('PDF iframe failed to load, fallback options available')
  }

  const generatePDF = async (listData, options = {}) => {
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })

      const pageWidth = pdf.internal.pageSize.getWidth()
      const margin = 15
      const contentWidth = pageWidth - (2 * margin)
      let yPosition = margin

      // Set font
      pdf.setFont('helvetica')

      // Title
      if (options.showHeader !== false) {
        pdf.setFontSize(20)
        pdf.setFont('helvetica', 'bold')
        pdf.text(listData.name || 'Packlist', pageWidth / 2, yPosition, { align: 'center' })
        yPosition += 12

        if (listData.description) {
          pdf.setFontSize(12)
          pdf.setFont('helvetica', 'normal')
          const lines = pdf.splitTextToSize(listData.description, contentWidth)
          pdf.text(lines, pageWidth / 2, yPosition, { align: 'center' })
          yPosition += (lines.length * 6) + 5
        }

        // Metadata
        pdf.setFontSize(10)
        const metadata = [
          new Date().toLocaleDateString(),
          `${listData.duration || 1} day${(listData.duration || 1) !== 1 ? 's' : ''}`,
          `${listData.items?.length || 0} items`
        ].join(' • ')
        pdf.text(metadata, pageWidth / 2, yPosition, { align: 'center' })
        yPosition += 8

        // Separator line
        pdf.line(margin, yPosition, pageWidth - margin, yPosition)
        yPosition += 8
      }

      // Group items by group
      const groupedItems = {}
      const items = listData.items || []
      
      items.forEach(item => {
        const groupName = item.group?.name || 'General'
        if (!groupedItems[groupName]) {
          groupedItems[groupName] = []
        }
        groupedItems[groupName].push(item)
      })

      // Process groups
      for (const [groupName, groupItems] of Object.entries(groupedItems)) {
        // Group header
        pdf.setFontSize(14)
        pdf.setFont('helvetica', 'bold')
        pdf.text(groupName, margin, yPosition)
        yPosition += 8

        // Draw underline
        pdf.setLineWidth(0.5)
        pdf.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2)
        yPosition += 3

        // Group items
        pdf.setFontSize(10)
        pdf.setFont('helvetica', 'normal')

        for (const item of groupItems) {
          // Check if we need a new page
          if (yPosition > 270) {
            pdf.addPage()
            yPosition = margin
          }

          // Checkbox
          pdf.rect(margin, yPosition - 3, 3, 3)
          if (item.is_checked && options.showChecked !== false) {
            pdf.text('✓', margin + 0.5, yPosition)
          }

          // Item name with quantity
          let itemText = item.name
          const quantity = item.quantity_type === 'per_days' 
            ? (item.quantity * (listData.duration || 1))
            : item.quantity
          if (quantity > 1) {
            itemText += ` (${quantity}x)`
          }

          const x = margin + 5
          if (item.is_checked && options.showChecked !== false) {
            pdf.setTextColor(150, 150, 150)
            pdf.text(itemText, x, yPosition)
            // Add strikethrough line
            const textWidth = pdf.getTextWidth(itemText)
            pdf.line(x, yPosition - 1, x + textWidth, yPosition - 1)
            pdf.setTextColor(0, 0, 0) // Reset color
          } else {
            pdf.text(itemText, x, yPosition)
          }

          yPosition += 5

          // Item description
          if (item.description) {
            pdf.setFontSize(8)
            pdf.setTextColor(100, 100, 100)
            const descLines = pdf.splitTextToSize(item.description, contentWidth - 10)
            pdf.text(descLines, x + 2, yPosition)
            yPosition += (descLines.length * 3) + 1
            pdf.setFontSize(10)
            pdf.setTextColor(0, 0, 0)
          }
        }

        yPosition += 5 // Space after group
      }

      // Download the PDF
      const filename = options.filename || `${listData.name || 'packlist'}_${new Date().toISOString().split('T')[0]}.pdf`
      pdf.save(filename)

    } catch (error) {
      console.error('Error generating PDF:', error)
      throw new Error('Failed to generate PDF. Please try again.')
    }
  }

  return {
    showPDFPreview,
    previewPDFUrl,
    exportToPDF,
    generatePDF,
    downloadPDF,
    printPDF,
    closePDFPreview,
    handlePDFError
  }
}